import * as THREE from "three";
import { NEUTRAL_BUILD, type RiderBuild } from "../styles";
import { inked, toon } from "../toon";
import { frayGeometry, frayMaterial, jortsLegGeometry } from "./denim-natural";
import { FEET } from "./feet";
import { cachedShape, facets } from "./geom";
import { buildNaturalHand } from "./hand-natural";
import { FOREARM, SHIN, THIGH, UPPER_ARM, type Arm, type Leg, type SkaterLook } from "./look";
import { capsule, cloth, limb, limbProfile, limbRadius, skinMat, stretch } from "./materials";
import { buildPatch } from "./outfit";
import { buildArmPatch } from "./outfit-natural";

// Arms, hands, legs and tattoo sleeves.

/** A cartoon mitten hand: a round palm, a thumb and a knuckle ridge. */
export function buildHand(skin: THREE.Material): THREE.Group {
  const hand = new THREE.Group();
  hand.name = "hand";
  const palm = inked(new THREE.SphereGeometry(0.062, facets(16), facets(12, 2)), skin, 0.012);
  palm.scale.set(0.9, 1.15, 0.75);
  hand.add(palm);
  const fingers = inked(new THREE.CapsuleGeometry(0.032, 0.05, facets(4, 1), facets(10)), skin, 0.01);
  fingers.rotation.x = Math.PI / 2;
  fingers.position.set(-0.012, -0.055, 0);
  hand.add(fingers);
  const thumb = inked(new THREE.CapsuleGeometry(0.02, 0.035, facets(4, 1), facets(8)), skin, 0.008);
  thumb.position.set(-0.05, -0.01, 0);
  thumb.rotation.z = -0.7;
  hand.add(thumb);
  return hand;
}

/** Leggings (#227) hug the leg: this much of the build's leg girth. */
export const LEGGINGS_FIT = 0.9;

/** Baggy pants (#234) hang loose: this much of the build's leg girth, wider than pants. */
export const BAGGY_FIT = 1.25;
/** Where a baggy leg's hem stops above the ankle, breaking over the shoe's collar. */
export const BAGGY_HEM_UP = 0.03;

/**
 * A baggy leg from the knee: a gentle taper down to a hem that flares a
 * little where it breaks over the shoe, its bottom closed flat above the
 * sole so a wide leg never pokes through the board.
 */
export function baggyShin(top: number, hem: number, mat: THREE.Material): THREE.Mesh {
  const len = SHIN - BAGGY_HEM_UP;
  const pts = [new THREE.Vector2(1e-4, -len + 0.004), new THREE.Vector2(hem * 0.8, -len), new THREE.Vector2(hem * 1.08, -len + 0.012)];
  // The side, from just above the break up to the knee.
  pts.push(...limbProfile(top, hem, len, 0, 0.4).slice(2));
  // Top cap: from the knee's equator up to the tip, like `limb`'s.
  const CAP = 6;
  for (let i = 1; i <= CAP; i++) {
    const a = (i / CAP) * (Math.PI / 2);
    pts.push(new THREE.Vector2(Math.max(Math.cos(a) * top, 1e-4), Math.sin(a) * top));
  }
  return inked(new THREE.LatheGeometry(pts, 16), mat);
}

export function buildLeg(look: SkaterLook, z: number, build: RiderBuild = NEUTRAL_BUILD): Leg {
  const cutoffs = look.bottoms.style === "cutoffs";
  const leggings = look.bottoms.style === "leggings";
  const baggy = look.bottoms.style === "baggy";
  const girth = build.limbs * (leggings ? LEGGINGS_FIT : baggy ? BAGGY_FIT : 1);
  const tapered = build.joints === "tapered";
  const hip = new THREE.Group();
  hip.position.set(0, 0, z * 0.5);
  // Baggy pants are plain cloth, not denim or stretch.
  const legMat = () => (leggings ? stretch(look.bottoms.color) : cloth(look.bottoms.color, !baggy));
  const thighMat = cutoffs ? skinMat(look.skin) : legMat();
  // Tapered: hip to knee, a little swell up top; the shin starts as wide as the knee.
  const thigh = !tapered
    ? capsule(0.085 * girth, THIGH - 0.1, thighMat)
    : baggy
      ? limb(0.09 * girth, 0.078 * girth, THIGH, thighMat, 0.04, 0.3)
      : limb(0.092 * girth, 0.068 * girth, THIGH, thighMat, 0.08, 0.3);
  thigh.name = "thigh";
  hip.add(thigh);
  if (cutoffs && tapered) {
    // Jorts (#75): a leg flaring a little to a frayed hem, at the toy's cuff's child index.
    const leg = new THREE.Mesh(cachedShape(`jorts-leg ${girth}`, () => jortsLegGeometry(girth)), cloth(look.bottoms.color, true, { side: THREE.DoubleSide }));
    leg.name = "shorts-leg";
    hip.add(leg);
    const fray = new THREE.Mesh(cachedShape(`fray ${girth}`, () => frayGeometry(girth)), frayMaterial());
    fray.name = "fray";
    hip.add(fray);
  } else if (cutoffs) {
    // Denim cut-offs: a wide leg over the top two thirds of the thigh, a
    // lighter frayed cuff at the hem, knee and shin bare.
    const len = THIGH * 0.66;
    const legGeo = new THREE.CylinderGeometry(0.108 * girth, 0.1 * girth, len, facets(14), 1, true);
    legGeo.translate(0, -len / 2 + 0.06, 0);
    const leg = inked(legGeo, cloth(look.bottoms.color, true, { side: THREE.DoubleSide }), 0.012);
    leg.name = "shorts-leg";
    hip.add(leg);
    const cuff = new THREE.Mesh(new THREE.TorusGeometry(0.1 * girth, 0.016, facets(8, 2), facets(20)), cloth("#c9d6ee", true));
    cuff.rotation.x = Math.PI / 2;
    cuff.position.y = -len + 0.06;
    hip.add(cuff);
  }
  const knee = new THREE.Group();
  knee.position.y = -THIGH;
  const shinMat = cutoffs ? skinMat(look.skin) : legMat();
  // Tapered: a calf just below the knee, narrowing to the ankle inside the shoe.
  const shin = !tapered
    ? capsule(0.075 * girth, SHIN - 0.1, shinMat)
    : baggy
      ? baggyShin(0.078 * girth, 0.064 * girth, shinMat)
      : limb(0.068 * girth, 0.046 * girth, SHIN, shinMat, 0.16, 0.3);
  shin.name = "shin";
  knee.add(shin);
  const shoe = FEET[build.feet][look.shoeStyle ?? "skate"](look.shoes);
  const foot = new THREE.Group();
  shoe.position.set(-0.03, 0, 0);
  if (build.shoes.length !== 1 || build.shoes.profile !== 1) shoe.scale.set(build.shoes.length, build.shoes.profile, build.shoes.profile);
  foot.add(shoe);
  foot.position.y = -SHIN;
  knee.add(foot);
  if (baggy && !tapered) {
    // The toy's capsule shin gets a hem flaring a little as it breaks over the shoe, after the foot so the joints keep their indexes.
    const hemGeo = new THREE.CylinderGeometry(0.075 * girth, 0.084 * girth, 0.07, facets(16), 1, true);
    hemGeo.translate(0, -SHIN + BAGGY_HEM_UP + 0.035, 0);
    const hem = inked(hemGeo, cloth(look.bottoms.color, false, { side: THREE.DoubleSide }), 0.01);
    hem.name = "hem";
    knee.add(hem);
  }
  hip.add(knee);
  return { hip, knee, foot, z };
}

/**
 * A tattoo sleeve: a thin shell over the arm with a canvas texture of
 * flash-style designs (star, heart, waves) on a transparent background.
 * Over a tapered arm (`profile`, the arm's side) it follows the bicep, a
 * little proud of the skin.
 */
export function buildTattooSleeve(ink: string, girth = 1, profile?: THREE.Vector2[]): THREE.Mesh {
  const len = UPPER_ARM;
  let geo: THREE.BufferGeometry;
  if (profile) {
    geo = new THREE.LatheGeometry(
      profile.map((p) => new THREE.Vector2(p.x * 1.06, p.y)),
      facets(16),
    );
  } else {
    geo = new THREE.CylinderGeometry(0.064 * girth, 0.064 * girth, len, facets(16), 1, true);
    geo.translate(0, -len / 2 - 0.02, 0);
  }
  const tex = tattooTexture(ink);
  const mat = toon("#ffffff", { map: tex, transparent: true, alphaTest: 0.3, depthWrite: false });
  if (!tex) mat.visible = false;
  const sleeve = new THREE.Mesh(geo, mat);
  sleeve.name = "tattoo";
  return sleeve;
}

export function tattooTexture(ink: string): THREE.CanvasTexture | null {
  if (typeof document === "undefined" || /jsdom/i.test(navigator.userAgent)) return null;
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.strokeStyle = ink;
  ctx.fillStyle = ink;
  ctx.lineWidth = 7;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  // The canvas wraps around the arm (u) from shoulder (v top) to elbow.
  // Two copies around the arm so the design shows from any side.
  for (const x0 of [0, 128]) {
    // Star up top.
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const r = i % 2 ? 14 : 32;
      const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
      ctx.lineTo(x0 + 64 + Math.cos(a) * r, 48 + Math.sin(a) * r);
    }
    ctx.closePath();
    ctx.fill();
    // Heart in the middle, outlined.
    ctx.beginPath();
    ctx.moveTo(x0 + 64, 150);
    ctx.bezierCurveTo(x0 + 20, 120, x0 + 30, 90, x0 + 64, 108);
    ctx.bezierCurveTo(x0 + 98, 90, x0 + 108, 120, x0 + 64, 150);
    ctx.stroke();
    // Waves at the bottom.
    for (const y of [190, 220]) {
      ctx.beginPath();
      for (let x = 0; x <= 128; x += 4) ctx.lineTo(x0 + x, y + Math.sin((x / 128) * Math.PI * 4) * 8);
      ctx.stroke();
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * An arm in two pieces: the upper arm (and any tattoo sleeve) from the
 * shoulder, then a rounded elbow, the forearm and the hand.
 */
export function buildArm(look: SkaterLook, side: "front" | "back", torso: THREE.Group, build: RiderBuild = NEUTRAL_BUILD): Arm {
  const girth = build.limbs;
  const tapered = build.joints === "tapered";
  const z = (side === "front" ? 1 : -1) * (look.build === "narrow" ? 0.185 : 0.2);
  const bare = look.top.style === "tank";
  // A T-shirt's short sleeves stop at the elbow: bare from there down.
  const short = look.top.style === "tshirt";
  const sleeveMat = bare ? skinMat(look.skin) : cloth(look.top.color);
  const shoulder = new THREE.Group();
  shoulder.name = `arm-${side}`;
  shoulder.position.set(0, 0.42, z);
  // Tapered: shoulder to elbow with a bicep; the forearm starts as wide as the elbow.
  const upperShape: Parameters<typeof limbProfile> = [0.062 * girth, 0.05 * girth, UPPER_ARM, 0.12, 0.4];
  const upper = tapered ? limb(upperShape[0], upperShape[1], upperShape[2], sleeveMat, upperShape[3], upperShape[4]) : capsule(0.06 * girth, UPPER_ARM - 0.05, sleeveMat);
  upper.name = "upper-arm";
  shoulder.add(upper);
  const tattoo = bare ? look.tattoos.find((t) => t.arm === side) : undefined;
  if (tattoo) shoulder.add(buildTattooSleeve(tattoo.ink, girth, tapered ? limbProfile(...upperShape) : undefined));
  // A mission patch on the space suit's shoulder.
  if (look.top.style === "spacesuit" && side === "front") {
    const color = look.top.trim ?? "#e63946";
    // Tapered (#75): wrapped round the arm's outer side instead of a disc in front of it.
    shoulder.add(
      tapered ? buildArmPatch(color, Math.sign(z), (y) => limbRadius(upperShape[0], upperShape[1], -y / UPPER_ARM, upperShape[3], upperShape[4])) : buildPatch(color, 0.045, 0.062 * girth),
    );
  }
  const elbow = new THREE.Group();
  elbow.name = "elbow";
  elbow.position.y = -UPPER_ARM;
  // A ball at the joint so the bend reads as a round elbow, not a kink; tapered limbs round it off themselves.
  if (!tapered) elbow.add(inked(new THREE.SphereGeometry(0.058 * girth, facets(14), facets(10, 2)), short ? skinMat(look.skin) : sleeveMat, 0.01));
  const forearmMat = bare || short ? skinMat(look.skin) : sleeveMat;
  const forearm = tapered ? limb(0.05 * girth, 0.034 * girth, FOREARM, forearmMat, 0.14, 0.25) : capsule(0.052 * girth, FOREARM - 0.1, forearmMat);
  forearm.name = "forearm";
  elbow.add(forearm);
  const hand = build.hands === "fingers" ? buildNaturalHand(skinMat(look.skin)) : buildHand(skinMat(look.skin));
  hand.position.y = -FOREARM;
  elbow.add(hand);
  shoulder.add(elbow);
  torso.add(shoulder);
  // Elbows point back and out, away from the body.
  return { shoulder, elbow, hand, pole: new THREE.Vector3(0.8, 0.2, Math.sign(z) * 0.6).normalize() };
}
